import { Appearance, Background, Design, Placement, Size } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/UseScenarioPlanningToolBar.scss';
import * as Dfns from 'date-fns/fp';
import { humanize } from 'inflected';
import * as R from 'ramda';
import { createPortal } from 'react-dom';
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactElement,
  Dispatch,
  SetStateAction,
  ComponentType
} from 'react';
import { MdCalendarToday, MdOutlineFileDownload, MdOutlineHistory } from 'react-icons/md';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { PageToolbarSlotContext } from '@Components/Page/PageToolbarSlot';

import {
  AdjustModal,
  AdjustModalProps,
  PLAN_OPTIONS
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/AdjustModal';
import {
  SetPlanLengthModal,
  convertPlanLengthToWeeks,
  SetPlanLengtModalProps
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/SetPlanLengthModal';
import {
  capitalizeNames,
  GROUP_BY_ENUM,
  GROUP_BY_OPTIONS
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { ScenarioPlanningPresets } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningPresets';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  matchPresetKey,
  useScenarioPlanningPresets
} from '@Pages/GeoMMM/ScenarioPlanning/useScenarioPlanningPresets';
import { useSetError } from '../../../redux/modals';

interface UseScenarioPlanningToolBar {
  end: string;
  groupBy: GROUP_BY_ENUM;
  outcomeVariable: string | undefined;
  planLength: number;
  planLengthLabel: string;
  setGroupBy: Dispatch<SetStateAction<GROUP_BY_ENUM>>;
  setOutcomeVariable: Dispatch<SetStateAction<string | undefined>>;
  setPlanLength: Dispatch<SetStateAction<number>>;
  start: string;
  toolbar: ReactElement;
}
export interface EfficientFrontierDefaultDates {
  end: string;
  planLength: number;
  start: string;
}

interface NewPlanSettings {
  start: string;
  end: string;
  planLength: number;
}

interface UseScenarioPlanningToolBarArgs {
  // Second arg is optional — EfficientFrontier doesn't need the new settings
  // since it manages its own range state separately.
  adjustScenario?: (option: PLAN_OPTIONS, newPlanSettings?: NewPlanSettings) => void;
  AdjustModalComponent?: ComponentType<AdjustModalProps>;
  hasScenario?: boolean;
  // DEV-24557: tinuititest only has one outcome variable and doesn't need the picker at all.
  hideOutcomeVariable?: boolean;
  maxEnd?: string;
  minStart?: string;
  onExportData?: () => void;
  onReopenChangeImpact?: () => void;
  outcomeVariables?: string[];
  PlanLengthModalComponent?: ComponentType<SetPlanLengtModalProps>;
  // Portal the toolbar into the Page's fixed slot. Callers kept mounted while hidden must pass
  // false when inactive, or the toolbar shows on other tabs.
  pinToPageChrome?: boolean;
  showEfficientFrontierControls?: boolean;
  showScenarioPlanningControls?: boolean;
}

const SP_DEFAULT_DATES = {
  start: Dfns.format('yyyy-MM-dd')(Dfns.subMonths(1, new Date())),
  end: TODAY,
  planLength: 4
};

export const useScenarioPlanningToolBar = ({
  adjustScenario,
  AdjustModalComponent = AdjustModal,
  hasScenario = false,
  hideOutcomeVariable = false,
  maxEnd,
  minStart,
  onExportData,
  onReopenChangeImpact,
  outcomeVariables = [],
  PlanLengthModalComponent = SetPlanLengthModal,
  pinToPageChrome = false,
  showEfficientFrontierControls = false,
  showScenarioPlanningControls = true
}: UseScenarioPlanningToolBarArgs): UseScenarioPlanningToolBar => {
  const [groupBy, setGroupBy] = useState<GROUP_BY_ENUM>(GROUP_BY_ENUM.CHANNEL);
  const [isInitDone, setIsInitDone] = useState<boolean>(false);
  const [outcomeVariable, setOutcomeVariable] = useState<string>();
  const [planLength, setPlanLength] = useState<number>(SP_DEFAULT_DATES.planLength);
  const [range, setRange] = useState<DateRange>({
    start: SP_DEFAULT_DATES.start,
    end: SP_DEFAULT_DATES.end
  });
  const [pendingChange, setPendingChange] = useState<{
    planLength: number;
    start: string;
    end: string;
  } | null>(null);
  const [showAdjustModal, setShowAdjustModal] = useState<
    'Adjust Plan Length' | 'Adjust Reference Data Dates'
  >();
  const [showPlanLengthModal, setShowPlanLengthModal] = useState<boolean>(false);
  const setError = useSetError();
  const toolbarSlot = useContext(PageToolbarSlotContext);

  const maxWeeks = useMemo(
    () =>
      minStart && maxEnd
        ? R.min(52, Dfns.differenceInWeeks(new Date(minStart), new Date(maxEnd)))
        : undefined,
    [maxEnd, minStart]
  );

  // In SP mode the end is a Sunday, so the selectable upper bound is the Sunday ending
  // the last data week (endOfISOWeek of maxEnd's Monday); other modes keep maxEnd as-is.
  const endBound = useMemo(
    () =>
      maxEnd && showScenarioPlanningControls
        ? Dfns.format('yyyy-MM-dd')(Dfns.endOfISOWeek(Dfns.parseISO(maxEnd)))
        : maxEnd,
    [maxEnd, showScenarioPlanningControls]
  );

  const { presets } = useScenarioPlanningPresets({
    start: range.start,
    end: range.end,
    planLength,
    maxEnd,
    minStart
  });

  const planLengthLabel = ((): string => {
    const monthToWeekMap: Record<string, number> = {};
    for (let i = 1; i <= 12; i++) {
      monthToWeekMap[convertPlanLengthToWeeks(i, 'Month')] = i;
    }
    if (planLength === 52) {
      return '1 Year';
    }
    if (planLength % 13 === 0) {
      const scaledPlanLength = planLength / 13;
      return conditionallyPluralizeWithCount(scaledPlanLength, 'Quarter');
    }
    if (monthToWeekMap[planLength]) {
      const scaledPlanLength = monthToWeekMap[planLength];
      return conditionallyPluralizeWithCount(scaledPlanLength, 'Month');
    }
    return conditionallyPluralizeWithCount(planLength, 'Week');
  })();

  useEffect(() => {
    if (R.isNil(outcomeVariable) && outcomeVariables.length) {
      setOutcomeVariable(outcomeVariables[0]);
    }
  }, [outcomeVariable, outcomeVariables]);

  useEffect(() => {
    const newRange = R.clone(range);
    if (minStart && new Date(range.start) < new Date(minStart)) {
      newRange.start = minStart;
    }
    if (endBound && new Date(range.end) > new Date(endBound)) {
      newRange.end = endBound;
    }
    if (new Date(newRange.start) > new Date(newRange.end)) {
      if (minStart) {
        newRange.start = minStart;
      }
      if (endBound) {
        newRange.end = endBound;
      }
    }
    if (!isInitDone && minStart && maxEnd) {
      // Mirror the "Most Recent Data" preset: use parseISO (local) instead of
      // `new Date` (UTC) so the result doesn't drift back a day in timezones
      // west of UTC and then snap back another week in the Monday snap below. In SP mode the
      // end is a Sunday, so anchor on its Monday and go back planLength-1 weeks (inclusive count).
      newRange.start = Dfns.format('yyyy-MM-dd')(
        showScenarioPlanningControls
          ? Dfns.subWeeks(planLength - 1, Dfns.startOfISOWeek(Dfns.parseISO(newRange.end)))
          : Dfns.subWeeks(planLength, Dfns.parseISO(newRange.end))
      );
      setIsInitDone(true);
    }
    if (Dfns.differenceInDays(new Date(newRange.start), new Date(newRange.end)) > 365) {
      newRange.start = Dfns.format('yyyy-MM-dd')(Dfns.subWeeks(52, new Date(newRange.end)));
    }
    if (showScenarioPlanningControls) {
      newRange.end = Dfns.format('yyyy-MM-dd')(Dfns.endOfISOWeek(Dfns.parseISO(newRange.end)));
      newRange.start = Dfns.format('yyyy-MM-dd')(
        Dfns.startOfISOWeek(Dfns.parseISO(newRange.start))
      );
      if (minStart && new Date(newRange.start) < new Date(minStart)) {
        const parsedMin = Dfns.parseISO(minStart);
        newRange.start = Dfns.isMonday(parsedMin)
          ? minStart
          : Dfns.format('yyyy-MM-dd')(Dfns.addWeeks(1, Dfns.startOfISOWeek(parsedMin)));
      }
    }
    if (!R.equals(range, newRange)) {
      setRange(newRange);
    }
  }, [isInitDone, minStart, maxEnd, endBound, planLength, range, showScenarioPlanningControls]);

  const planLengthModal = useMemo(
    () =>
      showPlanLengthModal && (
        <PlanLengthModalComponent
          currentPlanLength={planLength}
          maxWeeks={maxWeeks}
          onApply={newPlanLength => {
            setShowPlanLengthModal(false);
            const currentStart = new Date(range.start);
            const currentEnd = new Date(range.end);
            // In SP mode the end is a Sunday; anchor week math on its Monday and use an
            // inclusive span (planLength-1 gaps between the start and end Mondays).
            const endAnchor = showScenarioPlanningControls
              ? Dfns.startOfISOWeek(currentEnd)
              : currentEnd;
            const span = showScenarioPlanningControls ? newPlanLength - 1 : newPlanLength;
            const currentDiff =
              Dfns.differenceInWeeks(currentStart, endAnchor) +
              (showScenarioPlanningControls ? 1 : 0);
            const absoluteMax = new Date(R.defaultTo(TODAY, maxEnd));
            const absoluteMin = minStart ? new Date(minStart) : Dfns.subYears(2, new Date(TODAY));
            if (adjustScenario && currentDiff !== newPlanLength) {
              let newEndDate = endAnchor;
              let newStartDate = Dfns.subWeeks(span, endAnchor);
              if (Dfns.isBefore(absoluteMin, newStartDate)) {
                newStartDate = absoluteMin;
                newEndDate = Dfns.addWeeks(span, absoluteMin);
              }
              if (Dfns.isAfter(absoluteMax, newEndDate)) {
                newEndDate = absoluteMax;
                newStartDate = Dfns.subWeeks(span, absoluteMax);
              }
              // Mirror the normalization useEffect's snap (start→Monday, end→Sunday) so
              // pendingChange matches the range after setRange commits.
              if (showScenarioPlanningControls) {
                newStartDate = Dfns.startOfISOWeek(newStartDate);
                newEndDate = Dfns.endOfISOWeek(newEndDate);
                if (minStart) {
                  const parsedMin = Dfns.parseISO(minStart);
                  if (Dfns.isBefore(parsedMin, newStartDate)) {
                    newStartDate = Dfns.isMonday(parsedMin)
                      ? parsedMin
                      : Dfns.addWeeks(1, Dfns.startOfISOWeek(parsedMin));
                  }
                }
              }
              const formattedStart = Dfns.format('yyyy-MM-dd')(newStartDate);
              const formattedEnd = Dfns.format('yyyy-MM-dd')(newEndDate);
              if (hasScenario) {
                setPendingChange({
                  planLength: newPlanLength,
                  start: formattedStart,
                  end: formattedEnd
                });
                setShowAdjustModal('Adjust Reference Data Dates');
              } else {
                setPlanLength(newPlanLength);
                setRange({ start: formattedStart, end: formattedEnd });
              }
            } else {
              setPlanLength(newPlanLength);
            }
          }}
          onHide={() => setShowPlanLengthModal(false)}
        />
      ),
    [
      adjustScenario,
      hasScenario,
      minStart,
      maxEnd,
      maxWeeks,
      planLength,
      PlanLengthModalComponent,
      showPlanLengthModal,
      showScenarioPlanningControls,
      range
    ]
  );

  const adjustPlanLengthModal = useMemo(
    () =>
      showAdjustModal && (
        <AdjustModalComponent
          onApply={option => {
            setShowAdjustModal(undefined);
            // Pass settings explicitly (not via closure) so analyze sees the
            // committed range regardless of which flow staged the change.
            const newPlanSettings: NewPlanSettings = pendingChange ?? {
              start: range.start,
              end: range.end,
              planLength
            };
            if (pendingChange) {
              setPlanLength(pendingChange.planLength);
              setRange({ start: pendingChange.start, end: pendingChange.end });
            }
            if (adjustScenario) {
              adjustScenario(option, newPlanSettings);
            }
            setPendingChange(null);
          }}
          onHide={() => {
            setShowAdjustModal(undefined);
            setPendingChange(null);
          }}
          title={showAdjustModal}
        />
      ),
    [adjustScenario, AdjustModalComponent, pendingChange, planLength, range, showAdjustModal]
  );

  const toolbar = useMemo(
    () => (
      <div className='use-scenario-planning-tool-bar'>
        {adjustPlanLengthModal}
        {planLengthModal}
        <div className='left'>
          {(showScenarioPlanningControls || showEfficientFrontierControls) && (
            <Button
              size={Size.LG}
              className='plan-length-button'
              design={Design.NEUTRAL}
              onClick={() => setShowPlanLengthModal(true)}
              leadingElement={<MdCalendarToday />}
            >
              {planLengthLabel}
            </Button>
          )}
          {showScenarioPlanningControls && (
            <DatePicker
              design={Design.NEUTRAL}
              customPresets={({ start, end }) => (
                <ScenarioPlanningPresets
                  presets={presets}
                  selectedKey={matchPresetKey(
                    presets,
                    start ?? range.start ?? '',
                    end ?? range.end ?? ''
                  )}
                  onSelect={preset => setRange({ start: preset.start, end: preset.end })}
                />
              )}
              isOutsideRange={date =>
                R.isNil(minStart) ||
                new Date(date) < new Date(minStart) ||
                R.isNil(endBound) ||
                new Date(date) > new Date(endBound)
              }
              isDayBlocked={(date, focus) => {
                const parsed = Dfns.parseISO(date);
                // Picking the start (or starting fresh / over a complete range) → Mondays only;
                // once a start Monday is set (focus END) → Sundays only. No focus (text-input
                // validation) accepts either week boundary.
                if (focus === 'END') {
                  return !Dfns.isSunday(parsed);
                }
                if (focus === 'START') {
                  return !Dfns.isMonday(parsed);
                }
                return !Dfns.isMonday(parsed) && !Dfns.isSunday(parsed);
              }}
              fullWeeksOnly
              exclusiveEndWeek
              background={Background.LIGHT}
              onChange={newRange => {
                const { end, start } = newRange;
                if (
                  showScenarioPlanningControls &&
                  Dfns.differenceInDays(new Date(start), new Date(end)) > 365
                ) {
                  const message = 'Cannot set dates longer than a year for scenario planning';
                  setError({ message, reportError: new Error(message) });
                  return;
                }
                // Inclusive week count: end is a Sunday, so count Mondays from the start
                // week through the end's week (differenceInWeeks of the two Mondays + 1).
                const newPlanLength =
                  Dfns.differenceInWeeks(
                    Dfns.startOfISOWeek(Dfns.parseISO(start)),
                    Dfns.startOfISOWeek(Dfns.parseISO(end))
                  ) + 1;
                if (newPlanLength !== planLength && hasScenario) {
                  setShowAdjustModal('Adjust Plan Length');
                }
                setPlanLength(newPlanLength);
                setRange(newRange);
              }}
              range={range}
              appearance={Appearance.FILLED}
            />
          )}
          {showEfficientFrontierControls && (
            <DatePicker
              design={Design.NEUTRAL}
              isOutsideRange={
                minStart && maxEnd
                  ? date => new Date(date) < new Date(minStart) || new Date(date) > new Date(maxEnd)
                  : undefined
              }
              background={Background.LIGHT}
              onChange={newRange => {
                // DEV-24477: keep the "Plan Length" button label in sync with a direct Reference
                // Range edit, mirroring the Scenario Planning date picker's onChange above -- the
                // EF picker isn't `fullWeeksOnly`, so snap to ISO week boundaries first (as the SP
                // picker's own selections already do) before diffing in whole weeks.
                const { start: newStart, end: newEnd } = newRange;
                const newPlanLength =
                  Dfns.differenceInWeeks(
                    Dfns.startOfISOWeek(Dfns.parseISO(newStart)),
                    Dfns.startOfISOWeek(Dfns.parseISO(newEnd))
                  ) + 1;
                if (newPlanLength !== planLength) {
                  setPlanLength(newPlanLength);
                }
                setRange(newRange);
              }}
              range={range}
              removeHeaderText
              appearance={Appearance.FILLED}
              label='Reference Range'
            />
          )}
          {outcomeVariables.length > 0 && !hideOutcomeVariable && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              readOnly={outcomeVariables.length <= 1}
              label='Outcome Variable'
              onChange={variable => setOutcomeVariable(variable)}
              options={R.map(
                outcomeVar => ({
                  label: capitalizeNames(humanize(outcomeVar)),
                  value: outcomeVar
                }),
                outcomeVariables
              )}
              value={outcomeVariable}
            />
          )}
          {!showEfficientFrontierControls && !showScenarioPlanningControls && (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}

              label='Group by'
              onChange={val => setGroupBy(val)}
              options={GROUP_BY_OPTIONS}
              value={groupBy}
            />
          )}
        </div>
        <div className='right'>
          {showScenarioPlanningControls && onExportData && (
            <IconButton
              size={Size.LG}
              design={Design.NEUTRAL}
              onClick={onExportData}
              aria-label='Export scenario planning data'
              tooltip={{ text: 'Export data' }}
            >
              <MdOutlineFileDownload />
            </IconButton>
          )}
          {showScenarioPlanningControls && onReopenChangeImpact && (
            <Button
              size={Size.LG}
              design={Design.NEUTRAL}
              leadingElement={<MdOutlineHistory />}
              onClick={onReopenChangeImpact}
              aria-label='Reopen change impact'
              tooltip={{ text: 'Reopen change impact' }}
            />
          )}
        </div>
      </div>
    ),
    [
      adjustPlanLengthModal,
      endBound,
      groupBy,
      hasScenario,
      hideOutcomeVariable,
      maxEnd,
      minStart,
      onExportData,
      onReopenChangeImpact,
      outcomeVariable,
      outcomeVariables,
      planLength,
      planLengthLabel,
      planLengthModal,
      presets,
      range,
      setError,
      showEfficientFrontierControls,
      showScenarioPlanningControls
    ]
  );
  return {
    end: range.end,
    groupBy,
    outcomeVariable,
    planLength,
    planLengthLabel,
    setGroupBy,
    setOutcomeVariable,
    setPlanLength,
    start: range.start,
    toolbar: pinToPageChrome && toolbarSlot ? createPortal(toolbar, toolbarSlot) : toolbar
  };
};
